import { Flag, Globe, Icon } from "@norican/icons";
import { cn, Select } from "@norican/react";
import { useState } from "react";

import { type AppLanguage, useLanguage, useT } from "@/i18n";
import { logger } from "@/lib/logger";

export interface LanguageSwitcherProps {
  className?: string;
  /** Control size. `sm` for the header, `md` on a settings page. */
  size?: "sm" | "md";
  /**
   * Hide the language name below the `sm` breakpoint, leaving only the globe.
   * The name comes back from `sm` up; `aria-label` names the control either way.
   */
  compact?: boolean;
}

/**
 * Language picker for the app header.
 *
 * Each option is labelled with its own endonym ("Deutsch", "简体中文"), never a
 * translation of the language name — someone who has landed in the wrong
 * language must still be able to find their own. The trigger carries an
 * explicit `aria-label` because its visible content is a flag plus a short
 * name, which does not say what the control does, and the change is echoed
 * through a polite live region so screen-reader users get confirmation that
 * something happened at all: the rest of the page re-renders silently.
 */
export function LanguageSwitcher({
  className,
  size = "sm",
  compact = false,
}: LanguageSwitcherProps) {
  const { t } = useT();
  const { language, languages, changeLanguage } = useLanguage();
  // The language that was chosen, NOT the sentence announcing it.
  //
  // Translating at selection time used the catalogue that was still loaded —
  // the one being switched away from — and froze the result in state, so
  // switching Japanese to English announced in Japanese and the Japanese
  // sentence then sat on an English page until the next reload. Holding the
  // code and translating in render fixes both halves: it picks up the new
  // catalogue as soon as it lazy-loads, and re-renders with every later switch.
  const [announced, setAnnounced] = useState<AppLanguage | null>(null);

  // A picker offering one language is a control that cannot do anything. It is
  // hidden rather than disabled: a greyed-out switcher invites people to ask
  // why their language is missing, and the answer belongs in the rollout
  // communication, not in a tooltip. Adding a tag to LAUNCH_LANGUAGE_TAGS
  // brings this back on its own.
  if (languages.length <= 1) {
    return null;
  }

  const handleChange = (next: AppLanguage) => {
    logger.debug("[i18n] switcher selected", next);
    changeLanguage(next);
    setAnnounced(next);
  };

  const announcement =
    announced === null
      ? ""
      : t("language.changed", {
          language: languages.find((entry) => entry.tag === announced)?.endonym ?? announced,
        });

  return (
    <div className={cn("flex items-center", className)}>
      <Select.Root
        value={language}
        onValueChange={(next) => handleChange(next as AppLanguage)}
        items={languages.map((entry) => ({ label: entry.endonym, value: entry.tag }))}
      >
        <Select.Trigger
          size={size}
          aria-label={t("language.select")}
          // Compact = globe only below `sm`: the name is visually hidden and
          // the chevron (the trigger's last child) goes too, so the header's
          // right cluster fits a 390px phone without clipping the avatar.
          className={cn(compact && "max-sm:[&>span:last-child]:hidden")}
        >
          <Icon icon={Globe} size="sm" />
          <Select.Value className={cn(compact && "max-sm:sr-only")} />
        </Select.Trigger>
        <Select.Portal>
          <Select.Positioner sideOffset={6} align="end">
            <Select.Popup width="content">
              <Select.List>
                {languages.map((entry) => (
                  <Select.Item key={entry.tag} value={entry.tag}>
                    <span className="flex items-center gap-2">
                      <Flag code={entry.flagCode} size="sm" />
                      <Select.ItemText>{entry.endonym}</Select.ItemText>
                    </span>
                  </Select.Item>
                ))}
              </Select.List>
            </Select.Popup>
          </Select.Positioner>
        </Select.Portal>
      </Select.Root>
      <span aria-live="polite" role="status" className="sr-only">
        {announcement}
      </span>
    </div>
  );
}
